import { Box, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import CardStatusType from "enums/CardStatusType";
import { formatCashFull } from "utils/formatCash";
import { EMPTY_CELL } from "utils/cards/cardRowData";

export const CARD_BALANCE_BAR_WIDTH = 160;

interface CardBalanceBarProps {
  spent: number;
  limit: number;
  status: string;
  limitType?: string;
}

export const CardBalanceBar = ({
  spent,
  limit,
  status,
  limitType,
}: CardBalanceBarProps) => {
  const { t } = useTranslation();

  if (status !== CardStatusType.ACTIVE) {
    return (
      <Typography variant="body1" component="span">
        {EMPTY_CELL}
      </Typography>
    );
  }

  // A 0% bar would read as spent out.
  if (limit === Infinity) {
    return (
      <Typography variant="caption" component="span">
        <AllInclusiveIcon fontSize="inherit" /> {t("No limit")}
      </Typography>
    );
  }

  const positiveLimit = Math.abs(limit);
  const positiveSpent = Math.abs(spent);
  // Clamped: an over-limit card yields a negative width, a 0 limit divides to NaN.
  const available = Math.max(0, positiveLimit - positiveSpent);
  const progress =
    positiveLimit === 0 ? 0 : Math.min(100, (available / positiveLimit) * 100);

  const bar = (
    <Box sx={{ width: CARD_BALANCE_BAR_WIDTH }}>
      <Box
        sx={{
          height: 6,
          borderRadius: "var(--radius-full)",
          backgroundColor: "var(--color-bg-elevated)",
          overflow: "hidden",
          marginBottom: "var(--space-1)",
        }}
      >
        {/* The width is a per-row value, so it stays an inline style: an sx
            would mint an emotion class per distinct percentage. */}
        <Box
          data-testid="balance-bar-fill"
          style={{ width: `${progress}%` }}
          sx={{ height: "100%", backgroundColor: "var(--color-bg-brand)" }}
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography
          variant="caption"
          component="span"
          sx={{ color: "var(--color-text-brand)" }}
        >
          {t("AvailableAmount", {
            amount: formatCashFull(available, "usd", false),
          })}
        </Typography>
        <Typography
          variant="caption"
          component="span"
          sx={{ color: "var(--color-text-secondary)" }}
        >
          {t("OfAmount", {
            amount: formatCashFull(positiveLimit, "usd", false),
          })}
        </Typography>
      </Box>
    </Box>
  );

  return limitType ? <Tooltip title={t(limitType)}>{bar}</Tooltip> : bar;
};

export default CardBalanceBar;
